// @spec AST-STR-042
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { AssetConnectorsPage } from '@/features/settings/assets/connectors/pages/asset-connectors-page';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/asset-connectors')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assetConnectors'),
  }),
  component: AssetConnectorsRoute,
});

/**
 * Asset Connectors settings page route.
 *
 * Allows organization administrators to configure connections to external
 * asset sources like AWS, Azure, GCP, ServiceNow, or custom APIs.
 */
function AssetConnectorsRoute() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <div className="flex gap-4">
            <Skeleton className="h-10 w-44" />
            <Skeleton className="h-10 w-44" />
            <div className="flex-1" />
            <Skeleton className="h-10 w-36" />
          </div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl">
        <AssetConnectorsPage />
      </div>
    </div>
  );
}
